/**
 * 운전 snapshot 과 WebSocket 연결 상태 (계획서 §12.6, §13.6).
 *
 * 핵심 규칙: **sequence 가 끊기면 REST `GET /state` 로 전체 snapshot 을 다시 읽는다.**
 * DB 의 `simulation_events.sequence` 에는 구멍이 있지만(clock.updated 를 저장하지 않는다)
 * WebSocket 으로 받는 번호는 연속이므로, 끊김은 곧 결손이다.
 */

import { create } from "zustand";

import { fetchRunState } from "@/api/client";
import type { RunSnapshot, WsEnvelope } from "@/types/api";

export type ConnectionState = "idle" | "connecting" | "open" | "closed";

interface RunStore {
  snapshot: RunSnapshot | null;
  connection: ConnectionState;
  /** 현재 run 에서 수신한 최신 서버 sequence (REST 와 WebSocket 공통). */
  lastSequence: number;
  /** gap 을 감지해 snapshot 을 다시 읽은 횟수. 개발자 확인용이다. */
  recoveries: number;
  lastEventAt: string | null;

  setSnapshot: (snapshot: RunSnapshot) => void;
  setConnection: (state: ConnectionState) => void;
  applyEvent: (envelope: WsEnvelope) => Promise<void>;
  /** 전체 응답을 적용했으면 true, 이전 run/오래된 응답을 버렸으면 false. */
  reload: (runId: string) => Promise<boolean>;
}

export const useRunStore = create<RunStore>((set, get) => ({
  snapshot: null,
  connection: "idle",
  lastSequence: 0,
  recoveries: 0,
  lastEventAt: null,

  setSnapshot: (snapshot) => {
    const current = get();
    const sameRun = current.snapshot?.run_id === snapshot.run_id;
    const lastSequence = sameRun ? current.lastSequence : 0;
    // 응답 도착 순서는 서버 상태 순서와 다르다. 같은 번호의 전체 snapshot 은
    // clock 부분 갱신을 보완하므로 허용하지만, 과거 응답은 절대 적용하지 않는다.
    if (snapshot.ws_sequence !== undefined && snapshot.ws_sequence < lastSequence) return;
    set({
      snapshot,
      lastSequence: snapshot.ws_sequence ?? lastSequence,
    });
  },

  setConnection: (connection) => {
    set({ connection });
  },

  async reload(runId) {
    const requested = get();
    if (requested.snapshot && requested.snapshot.run_id !== runId) return false;
    const snapshot = await fetchRunState(runId);
    // reset/과거 seek 는 run 자체를 바꾼다. 진행 중이던 이전 run 의 요청도 버린다.
    const current = get();
    if (current.snapshot && current.snapshot.run_id !== runId) return false;
    if (snapshot.run_id !== runId) return false;
    // 구버전 응답에 번호가 없어도 요청 이후 수신한 최신 event 를 덮지 않는다.
    if ((snapshot.ws_sequence ?? requested.lastSequence) < current.lastSequence) return false;
    get().setSnapshot(snapshot);
    return true;
  },

  async applyEvent(envelope) {
    const { lastSequence, snapshot } = get();

    // reset 직후에는 이전 run 의 socket 이 잠시 살아 있다. 그 event 로 snapshot 을
    // 덮으면 새 run 화면이 옛 run 상태로 되돌아간다. 다른 run 의 event 는 버린다.
    if (snapshot && envelope.run_id !== snapshot.run_id) return;

    if (envelope.sequence <= lastSequence) return; // 재전송이나 순서 역전은 버린다

    // gap 복구를 기다리는 동안에도 이 번호보다 오래된 REST 응답은 거절한다.
    set({ lastSequence: envelope.sequence, lastEventAt: envelope.occurred_at });

    const expected = lastSequence + 1;
    if (lastSequence > 0 && envelope.sequence > expected) {
      // sequence 누락 — 부분 갱신으로는 복구할 수 없다. 전체를 다시 읽는다 (§12.6).
      set((state) => ({ recoveries: state.recoveries + 1 }));
      await get().reload(envelope.run_id);
      return;
    }

    // clock.updated 는 시계만 갱신해 요청을 아낀다. 나머지는 전체를 다시 읽는다.
    if (envelope.type === "clock.updated" && snapshot) {
      const payload = envelope.payload as RunSnapshot["clock"];
      set({ snapshot: { ...snapshot, clock: payload } });
      return;
    }
    await get().reload(envelope.run_id);
  },
}));
